import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';

import { agendaActivitySchema } from 'src/validations/validation-schemas';
import { useMutation } from 'react-query';

import useAxios from 'src/api/useAxios';

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";


// API stores topics as "a · b · c"; the form edits them one per line
export const topicsToLines = (desc) => (desc || '').split('·').map((t) => t.trim()).filter(Boolean).join('\n')
export const linesToTopics = (text) => (text || '').split('\n').map((t) => t.trim()).filter(Boolean).join(' · ')

// "08:30" / "08:30:00" pair -> "1 Hour 10 Mins", same wording as existing data; "" if not a positive span
export const calcDuration = (start, end) => {
  const mins = (t) => { const [h, m] = (t || '').split(':').map(Number); return h * 60 + m }
  const diff = mins(end) - mins(start)
  if (!(diff > 0)) return ''
  const h = Math.floor(diff / 60), m = diff % 60
  return [h && `${h} Hour${h > 1 ? 's' : ''}`, m && `${m} Min${m > 1 ? 's' : ''}`].filter(Boolean).join(' ')
}

const AddActivityModal = ({
  isOpen,
  toggle,
  day,
  handleResponse
    }) => {

    const [modalStyles, setModalStyles] = useState({
      zIndex: '1',
    });

  const onSubmit = () => {

    const startingTime = new Date(`2000-01-01T${values.starting_time}`);
    const endingTime = new Date(`2000-01-01T${values.ending_time}`);

    if (endingTime < startingTime){
      toast.error('Starting time cannot be after ending time')
    }
    else{
      addActivityMutation.mutate({ ...values, day, duration: calcDuration(values.starting_time, values.ending_time), description: linesToTopics(values.description) })
    }
  }

  let api = useAxios();

  const addActivity = (payload) => {
    return api.post('/agenda/list-create', payload)
  }

  const addActivityMutation = useMutation(addActivity, {
    onSuccess: (res) => {
      resetForm()
      handleResponse('success', res)
    },
    onError: (res) => {
      handleResponse('error', res)
    }
  })

  const modules = {
    toolbar: [
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['bold', 'italic', 'underline'],
      ['clean'],                  
      [
        { indent: '-1' },
        { indent: '+1' },
      ],
    ],
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm } = useFormik({
    initialValues: {
      title: "",
      speaker: "",
      starting_time: "",
      ending_time: "",
      duration: "",
      external_attendees: "",
      venue: "",
      description: ""
    },
    validationSchema: agendaActivitySchema,
    onSubmit,
    enableReinitialize: true,
  })

  return (
    <Modal isOpen={isOpen} toggle={toggle} className='modal-lg'>
      <ModalHeader toggle={toggle}>
        <h4 className='text-primary mb-0'>Add New Activity for {day}</h4>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
      <ModalBody>
          <div className='row'>
            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Title*</Label>
              <ReactQuill
                    theme="snow"
                    value={values.title}
                    modules={modules}
                    className={
                      errors.title && touched.title ? "ql-error" : 'border green-border'
                    }
                    onChange={(e) => setFieldValue("title", e)}
                  />
                  {errors.title && touched.title && (
                    <p className="text-danger text-start">{errors.title}</p>
                  )}
            </div>
            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Speaker</Label>
              <Input
                value={values.speaker}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                className={errors.speaker && touched.speaker ? "form-control form-control-icon is-invalid col-6" : "form-control form-control-icon green-border col-6"}
                id="speakerInput"
                placeholder="e.g. Tareq AlSadhan/ McKinsey"
                name="speaker"
              />
              {
                errors.speaker && touched.speaker && <div className="invalid-feedback">{errors.speaker}</div>
              }
            </div>
            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Starting Time*</Label>
              <Input
                value={values.starting_time}
                onChange={handleChange}
                onBlur={handleBlur}
                type="time"
                className={errors.starting_time && touched.starting_time ? "form-control form-control-icon is-invalid col-6" : "form-control form-control-icon green-border col-6"}
                id="starting_timeInput"
                name="starting_time"
              />
              {
                errors.starting_time && touched.starting_time && <div className="invalid-feedback">{errors.starting_time}</div>
              }
            </div>

            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Ending Time*</Label>
              <Input
                value={values.ending_time}
                onChange={handleChange}
                onBlur={handleBlur}
                type="time"
                className={errors.ending_time && touched.ending_time ? "form-control form-control-icon is-invalid col-6" : "form-control form-control-icon green-border col-6"}
                id="ending_timeInput"
                name="ending_time"
              />
              {
                errors.ending_time && touched.ending_time && <div className="invalid-feedback">{errors.ending_time}</div>
              }
            </div>
            


            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Duration</Label>
              <Input
                value={calcDuration(values.starting_time, values.ending_time)}
                type="text"
                className="form-control form-control-icon green-border col-6"
                id="durationInput"
                name="duration"
                placeholder='Calculated from starting and ending time'
                disabled
              />
            </div>

            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Note / External Attendees</Label>
              <Input
                value={values.external_attendees}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                className={errors.external_attendees && touched.external_attendees ? "form-control form-control-icon is-invalid col-6" : "form-control form-control-icon green-border col-6"}
                id="external_attendeesInput"
                name="external_attendees"
                placeholder='e.g. Open Arrival Window, 8 - 10 minute walk'
                />
              {
                errors.external_attendees && touched.external_attendees && <div className="invalid-feedback">{errors.external_attendees}</div>
              }
            </div>
           
            <div className='col-md-6 col-12'>
              <Label className="form-label text-start mt-3 mb-1">Venue</Label>
              <Input
                value={values.venue}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                className={errors.venue && touched.venue ? "form-control form-control-icon is-invalid col-6" : "form-control form-control-icon green-border col-6"}
                id="venueInput"
                name="venue"
                placeholder='Add venue'
              />
              {
                errors.venue && touched.venue && <div className="invalid-feedback">{errors.venue}</div>
              }
            </div>

            <div className='col-12'>
              <Label className="form-label text-start mt-3 mb-1">Topics (one per line)</Label>
              <Input
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                type="textarea"
                rows={4}
                className="form-control green-border"
                id="descriptionInput"
                name="description"
                placeholder={'Where SNB stands today\nOverview of business strategy\n2027 AOP and targets'}
              />
            </div>

          </div>

      </ModalBody>
      <ModalFooter>
        {
          addActivityMutation.isLoading ? 
          <p>Processing...</p>
          :
          <>
            <button onClick={toggle} type="button" className="btn admin-gray-btn">
          Close
        </button>
        <button type="submit" className="btn admin-green-btn">
          Save
        </button>
</>
        }
      </ModalFooter>
        </form>
    </Modal>
  );
};

export default AddActivityModal;
